import { useEffect, useRef, useState } from "react";
import { Volume2, Pause, X } from "lucide-react";

const RATE_KEY = "accessibility_speech_rate";
const AUTO_KEY = "accessibility_auto_read";

/** Get user-saved speech rate (0.6 .. 1.6), default 1.0 */
function getSpeechRate() {
  try {
    const r = parseFloat(localStorage.getItem(RATE_KEY) || "1.0");
    return isNaN(r) ? 1.0 : Math.min(2.0, Math.max(0.5, r));
  } catch { return 1.0; }
}

function isAutoReadEnabled() {
  try { return localStorage.getItem(AUTO_KEY) === "1"; } catch { return false; }
}

/**
 * Speak the genie's voice (slightly higher pitch + magical chime).
 * Used by the Aladdin lamp triage flow ONLY.
 */
export function speakGenieFR(text) {
  try {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    // Magical chime via Web Audio API
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      const playTone = (freq, start, dur) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.type = "sine";
        o.frequency.value = freq;
        g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
        g.gain.exponentialRampToValueAtTime(0.18, ctx.currentTime + start + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
        o.connect(g).connect(ctx.destination);
        o.start(ctx.currentTime + start);
        o.stop(ctx.currentTime + start + dur);
      };
      playTone(880, 0, 0.25);
      playTone(1175, 0.12, 0.3);
      playTone(1568, 0.24, 0.35);
    } catch { /* ignore audio errors */ }
    // Speak after the chime
    setTimeout(() => {
      const u = new SpeechSynthesisUtterance(text);
      u.lang = "fr-BE";
      u.rate = getSpeechRate() * 1.02;
      u.pitch = 1.25; // slightly higher pitch = playful genie vibe
      const voices = window.speechSynthesis.getVoices();
      const fr = voices.find((v) => v.lang?.toLowerCase().startsWith("fr")) || voices[0];
      if (fr) u.voice = fr;
      window.speechSynthesis.speak(u);
    }, 500);
  } catch { /* ignore */ }
}

/** Speak with the standard serious voice (for accessibility + verdicts). */
export function speakFR(text) {
  try {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "fr-BE";
    u.rate = getSpeechRate();
    u.pitch = 1.0;
    const voices = window.speechSynthesis.getVoices();
    const fr = voices.find((v) => v.lang?.toLowerCase().startsWith("fr")) || voices[0];
    if (fr) u.voice = fr;
    window.speechSynthesis.speak(u);
  } catch { /* ignore */ }
}

/**
 * Accessibility floating button: reads the current page aloud.
 * - Visible bottom-right, large, high-contrast
 * - Uses SpeechSynthesis (free, offline-capable)
 * - Respects user-saved speech rate
 * - Auto-reads page on mount if "accessibility_auto_read" is enabled
 */
export default function ReadAloudFAB({ targetSelector = "main" }) {
  const [state, setState] = useState("idle"); // idle | playing | paused
  const [autoMode, setAutoMode] = useState(isAutoReadEnabled());
  const startedAutoRef = useRef(false);

  // Refresh autoMode when user toggles in settings
  useEffect(() => {
    const onChange = () => setAutoMode(isAutoReadEnabled());
    window.addEventListener("accessibility-changed", onChange);
    return () => window.removeEventListener("accessibility-changed", onChange);
  }, []);

  // Stop speech on unmount
  useEffect(() => {
    return () => { try { window.speechSynthesis?.cancel(); } catch { /* ignore */ } };
  }, []);

  const extractReadableText = () => {
    const root = document.querySelector(targetSelector) || document.body;
    const clone = root.cloneNode(true);
    clone.querySelectorAll('[aria-hidden="true"], .no-read, script, style, button, input, textarea, select').forEach((el) => el.remove());
    return (clone.innerText || clone.textContent || "").replace(/\s+/g, " ").trim().slice(0, 6000);
  };

  const start = () => {
    if (!("speechSynthesis" in window)) {
      alert("La lecture vocale n'est pas supportée sur ce navigateur.");
      return;
    }
    const text = extractReadableText();
    if (!text) return;
    try { window.speechSynthesis.cancel(); } catch { /* ignore */ }
    const u = new SpeechSynthesisUtterance(text);
    u.lang = "fr-BE";
    u.rate = getSpeechRate();
    u.pitch = 1.0;
    const voices = window.speechSynthesis.getVoices();
    const fr = voices.find((v) => v.lang?.toLowerCase().startsWith("fr")) || voices[0];
    if (fr) u.voice = fr;
    u.onend = () => setState("idle");
    u.onerror = () => setState("idle");
    window.speechSynthesis.speak(u);
    setState("playing");
  };

  const pause = () => { try { window.speechSynthesis.pause(); setState("paused"); } catch { /* ignore */ } };
  const resume = () => { try { window.speechSynthesis.resume(); setState("playing"); } catch { /* ignore */ } };
  const stop = () => { try { window.speechSynthesis.cancel(); } catch { /* ignore */ } setState("idle"); };

  // Auto-read on mount if enabled (only once per page mount)
  useEffect(() => {
    if (!autoMode || startedAutoRef.current) return;
    startedAutoRef.current = true;
    // Slight delay so the page DOM is fully rendered
    const t = setTimeout(() => start(), 800);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoMode]);

  return (
    <div className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-40 flex flex-col items-end gap-2" data-testid="read-aloud-fab" aria-label="Lecture vocale de la page">
      {state !== "idle" && (
        <button
          type="button"
          onClick={stop}
          className="h-10 px-4 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-xs font-medium uppercase tracking-widest shadow-lg flex items-center gap-1.5"
          data-testid="read-aloud-stop"
          aria-label="Arrêter la lecture"
        >
          <X className="h-3.5 w-3.5" /> Arrêter
        </button>
      )}
      <button
        type="button"
        onClick={state === "idle" ? start : state === "playing" ? pause : resume}
        className="group relative h-14 sm:h-16 px-5 sm:px-6 rounded-full bg-gradient-to-br from-amber-500 via-orange-500 to-yellow-600 text-white shadow-xl hover:shadow-amber-500/50 hover:scale-105 active:scale-95 transition-all flex items-center gap-3 font-medium"
        data-testid="read-aloud-button"
        aria-label={state === "idle" ? "Lire cette page à voix haute" : state === "playing" ? "Mettre en pause" : "Reprendre la lecture"}
      >
        <span className="text-2xl group-hover:animate-pulse">🪔</span>
        <div className="text-left leading-tight">
          <p className="text-[10px] uppercase tracking-[0.2em] text-amber-100">
            {autoMode ? "Auto · " : ""}Accessibilité
          </p>
          <p className="text-sm sm:text-base">
            {state === "idle" && "Lis-moi la page"}
            {state === "playing" && "Pause"}
            {state === "paused" && "Reprendre"}
          </p>
        </div>
        {state === "playing" && (<span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-emerald-400 animate-pulse" />)}
        {state === "playing" ? <Pause className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
      </button>
    </div>
  );
}
